import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialBadge, type EditorialBadgeTone } from '../../report/editorial';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

/** Severity → EditorialBadge tone (replaces filled pill backgrounds). */
function severityTone(severity: string): EditorialBadgeTone {
  if (severity === 'Critical' || severity === 'High') return 'negative';
  if (severity === 'Medium') return 'warning';
  return 'positive';
}

export const EnvironmentalRiskMatrixSection: React.FC<{ report: SiteRagReport; label: string }> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const risks = report.environmentalRiskMatrix ?? [];
  const hasData = risks.length > 0;

  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      <div
        className={`grid gap-3 ${theme.bodyColumns === 2 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}
        data-report-block="environmental-risk-grid"
      >
        {risks.map((r, i) => (
          <div
            key={`env-risk-${i}`}
            className="pt-3 space-y-2"
            // P-GROUP Phase A (`row`) — each risk card is a BOUNDED atom
            // (title + category + mitigation + residual, well under a page):
            // without it pagedjs split a card at the page seam, stranding the
            // h4 risk title alone at the page foot (the environmental
            // monograph gate cell).
            data-layout-group="row"
            style={{ borderTop: `1px solid ${roleColor(theme, 'rule')}` }}
          >
            <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
              <h4
                className="text-sm font-bold m-0"
                style={{ color: roleColor(theme, 'ink'), flex: '1 1 150px', minWidth: 0 }}
                data-card-title="true"
              >
                {r.risk}
              </h4>
              <div className="flex items-center gap-2 shrink-0">
                <EditorialBadge theme={theme} tone={severityTone(r.severity)} variant="outline">
                  {r.severity}
                </EditorialBadge>
                <span className="text-xs" style={{ color: roleColor(theme, 'body') }}>{r.likelihood}</span>
              </div>
            </div>
            <div className="text-xs" style={{ color: roleColor(theme, 'muted') }}>{r.category}</div>
            <div className="pt-2" style={{ borderTop: `1px solid ${roleColor(theme, 'hair')}` }}>
              <div
                className="text-xs font-bold uppercase tracking-widest mb-1"
                style={{ fontFamily: MONO, fontSize: REPORT_TEXT_PX.caption, color: roleColor(theme, 'muted') }}
              >
                Mitigation
              </div>
              <p className="text-xs m-0 leading-relaxed" style={{ color: roleColor(theme, 'body') }}>{r.mitigation}</p>
            </div>
            <p className="text-xs m-0 leading-relaxed" style={{ color: roleColor(theme, 'body') }}>
              Residual risk: <span className="font-bold" style={{ color: roleColor(theme, 'ink') }}>{r.residualRisk}</span>
            </p>
          </div>
        ))}
      </div>
    </SectionShell>
  );
};
